import { FC } from 'react';
import { analyticsCommonEvents } from '@belong/analytics';
import { useAnalyticsEvents } from '@belong/providers/analyticsEvents';
import { FONT_COLOURS } from '@belong/themes';
import { IButton, ICalloutProps, IImage, ILink, ITestable } from '@belong/types';
import Callout from '../../feedback/Callout';
import { NODES as defaultRenderers, RichText } from '../../styles/Typography/RichText/RichText';
import { Column, Columns, Stack } from '../../ui';
import * as styles from './styles';
import type { Document } from '@contentful/rich-text-types';

export interface IPromoBanner extends ITestable {
  callout?: ICalloutProps;
  description?: Document;
  disclaimer?: Document;
  link?: ILink & IButton;
  image?: IImage;
  hideImageOnSmallDevices?: boolean;
  hasFullWidthContent?: boolean;
}

const PromoBanner: FC<IPromoBanner> = props => {
  const {
    callout,
    'data-testid': testid,
    description,
    disclaimer,
    link,
    image,
    hideImageOnSmallDevices,
    hasFullWidthContent
  } = props;
  const { buttonClick } = analyticsCommonEvents;
  const analyticsEvents = useAnalyticsEvents();
  const eventProps = {
    ...buttonClick({
      destination: 'internal',
      label: 'primary button',
      text: link?.label
    }),
    component: 'Banner'
  };

  // trigger analytics event when button is clicked
  // trigger onClick event if onclick is defined
  const ctaOnClick: any = ((evt: Event) => {
    evt.stopPropagation();
    analyticsEvents.send(eventProps);
    if (link?.onClick) {
      link.onClick?.(evt as any);
    }
  }) as any;

  return (
    <styles.Root showHoverEffect={!!link} data-testid={testid}>
      {link && <styles.LinkOverlay onClick={ctaOnClick} href={link?.href} tabIndex={-1} aria-hidden="true" />}
      <Columns collapseBelow="md" alignY="stretch">
        <Column width={{ md: hasFullWidthContent && !image ? '1' : '7/12' }}>
          <Stack space={{ xs: 'xsmall', md: 'medium' }}>
            {callout && <Callout {...callout} />}
            <Stack space="xsmall">
              {description && (
                <RichText
                  html={description}
                  hasColor={FONT_COLOURS.LIGHT}
                  h1="headingLarge"
                  h2="headingLarge"
                  h3="headingLarge"
                  h4="headingLarge"
                  h5="headingLarge"
                  h6="headingLarge"
                  spacing="small"
                />
              )}
              <Stack space={{ xs: 'medium', md: 'large' }}>
                {disclaimer && (
                  <styles.DisclaimerWrapper>
                    <RichText
                      html={disclaimer}
                      hasColor={FONT_COLOURS.LIGHT}
                      p="copySmall"
                      renderEmbeddedInlines={{
                        elementEmbeddedInformationModal: entryProps => {
                          return defaultRenderers.elementEmbeddedInformationModal(entryProps, { isLightColor: true });
                        }
                      }}
                    />
                  </styles.DisclaimerWrapper>
                )}
                {link && <styles.StyledButton {...link} onClick={ctaOnClick} isLightColor />}
              </Stack>
            </Stack>
          </Stack>
        </Column>
        {image && (
          <Column width={{ md: '5/12' }}>
            <styles.ImageArea hideImageOnSmallDevices={hideImageOnSmallDevices}>
              <styles.ImageWrapper>
                <styles.Image {...image} source={image.src} showHoverEffect={!!link} />
              </styles.ImageWrapper>
            </styles.ImageArea>
          </Column>
        )}
      </Columns>
    </styles.Root>
  );
};

PromoBanner.displayName = 'PromoBanner';
export default PromoBanner;
